import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { MagnifyingGlassIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { jiraAccountService } from '../../services/jiraAccountService';
import { jiraDataService } from '../../services/jiraDataService';
import { useUser } from '../../context/UserContext';
import { toast } from 'react-toastify';

const addMonths = (date, months) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
};

const formatDateForInput = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  return d.toISOString().slice(0, 10);
};

export default function ProjectFormModal({ onClose, onSubmit, loading = false, existingProjectKeys = [], editProject = null }) {
  const { activeJiraAccount } = useUser();
  const isEditMode = !!editProject;

  const [formData, setFormData] = useState({
    name: editProject?.name || '',
    jira_account_id: editProject?.jira_account_id ? String(editProject.jira_account_id) : '',
    jira_project_key: editProject?.jira_project_key || '',
    jira_project_name: editProject?.jira_project_name || '',
    start_date: editProject?.start_date ? formatDateForInput(new Date(editProject.start_date)) : '',
    end_date: editProject?.end_date ? formatDateForInput(new Date(editProject.end_date)) : '',
    dev_capacity_hours_per_month: editProject?.dev_capacity_hours_per_month || '',
    week_start_day: editProject?.week_start_day ?? 1,
  });
  const [errors, setErrors] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [jiraProjects, setJiraProjects] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [jiraProjectSearch, setJiraProjectSearch] = useState(editProject?.jira_project_name || '');
  const [showJiraProjectDropdown, setShowJiraProjectDropdown] = useState(false);
  const [loadingStartDate, setLoadingStartDate] = useState(false);
  const [jiraActualStartDate, setJiraActualStartDate] = useState(editProject?.jira_start_date || '');

  // Fetch jira actual start date in edit mode if not stored yet
  useEffect(() => {
    if (!isEditMode) return;
    if (jiraActualStartDate) return; // already have it
    if (!editProject?.jira_project_key) return;

    setLoadingStartDate(true);
    jiraDataService.getProjectStartDate(editProject.jira_project_key)
      .then((res) => {
        if (res?.start_date) {
          setJiraActualStartDate(res.start_date);
        }
      })
      .catch(() => { }) // fail silently, field just shows —
      .finally(() => setLoadingStartDate(false));
  }, []);

  const fetchAccounts = useCallback(async () => {
    setLoadingAccounts(true);
    try {
      const res = await jiraAccountService.getAccounts();
      const list = Array.isArray(res?.data) ? res.data : [];
      setAccounts(list);
    } catch (err) {
      setErrors((e) => ({ ...e, jira_account_id: 'Failed to load Jira accounts' }));
      toast.error(err?.message || 'Failed to load Jira accounts');
    } finally {
      setLoadingAccounts(false);
    }
  }, []);

  const fetchJiraProjects = useCallback(async () => {
    if (!activeJiraAccount) {
      setJiraProjects([]);
      return;
    }
    setLoadingProjects(true);
    try {
      const res = await jiraDataService.getProjects();
      const list = res?.data ?? (Array.isArray(res) ? res : []);
      setJiraProjects(Array.isArray(list) ? list : []);
    } catch (err) {
      setJiraProjects([]);
      setErrors((e) => ({ ...e, jira_project_key: 'Failed to load Jira projects' }));
    } finally {
      setLoadingProjects(false);
    }
  }, [activeJiraAccount]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const hasPreselectedAccount = useRef(false);
  useEffect(() => {
    if (activeJiraAccount && accounts.length > 0 && !hasPreselectedAccount.current) {
      hasPreselectedAccount.current = true;
      if (!isEditMode) {
        setFormData((prev) => ({ ...prev, jira_account_id: String(activeJiraAccount.id) }));
      }
    }
  }, [activeJiraAccount, accounts, isEditMode]);

  useEffect(() => {
    if (formData.jira_account_id && activeJiraAccount?.id === Number(formData.jira_account_id)) {
      fetchJiraProjects();
    } else {
      setJiraProjects([]);
      if (!isEditMode) {
        setJiraProjectSearch('');
        setFormData((prev) => ({ ...prev, jira_project_key: '', jira_project_name: '', start_date: '' }));
      }
    }
  }, [formData.jira_account_id, activeJiraAccount?.id, fetchJiraProjects, isEditMode]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !loading) {
        if (showJiraProjectDropdown) {
          setShowJiraProjectDropdown(false);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [loading, onClose, showJiraProjectDropdown]);

  const jiraProjectRef = useRef(null);
  useEffect(() => {
    if (!showJiraProjectDropdown) return;
    const handleClickOutside = (e) => {
      if (jiraProjectRef.current && !jiraProjectRef.current.contains(e.target)) {
        setShowJiraProjectDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showJiraProjectDropdown]);

  const existingKeysSet = useMemo(
    () => new Set((existingProjectKeys).map((k) => String(k).toLowerCase())),
    [existingProjectKeys]
  );

  const filteredJiraProjects = useMemo(() =>
    jiraProjects
      .filter(
        (p) =>
          !jiraProjectSearch ||
          (p.name || '').toLowerCase().includes(jiraProjectSearch.toLowerCase()) ||
          (p.key || '').toLowerCase().includes(jiraProjectSearch.toLowerCase())
      )
      .map((p) => ({
        ...p,
        isExisting: existingKeysSet.has(String(p.key).toLowerCase()),
      })),
    [jiraProjects, jiraProjectSearch, existingKeysSet]
  );

  const startDateRequestRef = useRef(0);

  const handleSelectJiraProject = async (project) => {
    const requestId = ++startDateRequestRef.current;

    setFormData((prev) => ({
      ...prev,
      jira_project_key: project.key || project.id,
      jira_project_name: project.name || project.key,
      start_date: '',
      end_date: '',
    }));
    setJiraProjectSearch(project.name || project.key || '');
    setShowJiraProjectDropdown(false);

    setLoadingStartDate(true);
    try {
      const res = await jiraDataService.getProjectStartDate(project.key);
      if (requestId !== startDateRequestRef.current) return; // stale, ignore

      const startDate = res?.start_date ? new Date(res.start_date) : new Date();
      const endDate = addMonths(startDate, 3);
      const formattedStart = formatDateForInput(startDate);
      setJiraActualStartDate(formattedStart);
      setFormData((prev) => ({
        ...prev, 
        start_date: formattedStart,
        end_date: formatDateForInput(endDate),
      }));
    } catch (err) {
      if (requestId !== startDateRequestRef.current) return; // stale, ignore

      const fallback = new Date();
      setFormData((prev) => ({
        ...prev,
        start_date: formatDateForInput(fallback),
        end_date: formatDateForInput(addMonths(fallback, 3)),
      }));
      toast.error(err.message || 'Failed to fetch project start date');
    } finally {
      if (requestId === startDateRequestRef.current) {
        setLoadingStartDate(false);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.name?.trim()) newErrors.name = 'Project name is required';
    if (!isEditMode && !formData.jira_account_id) newErrors.jira_account_id = 'Jira account is required';
    if (!isEditMode && !formData.jira_project_key) newErrors.jira_project_key = 'Jira project is required';
    if (!formData.dev_capacity_hours_per_month || Number(formData.dev_capacity_hours_per_month) <= 0) {
      newErrors.dev_capacity_hours_per_month = 'Development capacity (hours/month) is required';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const startDate = formData.start_date || formatDateForInput(new Date());
    let endDate = formData.end_date;
    if (!endDate) {
      endDate = formatDateForInput(addMonths(new Date(startDate), 3));
    }

    onSubmit({
      name: formData.name.trim(),
      jira_account_id: Number(formData.jira_account_id),
      jira_project_key: formData.jira_project_key,
      jira_project_name: formData.jira_project_name,
      start_date: startDate,
      end_date: endDate,
      dev_capacity_hours_per_month: Number(formData.dev_capacity_hours_per_month),
      week_start_day: formData.week_start_day,
      jira_start_date: jiraActualStartDate || null,
    });
  };

  const isFormValid = isEditMode
    ? formData.name?.trim() &&
    formData.dev_capacity_hours_per_month &&
    Number(formData.dev_capacity_hours_per_month) > 0
    : formData.name?.trim() &&
    formData.jira_account_id &&
    formData.jira_project_key &&
    formData.dev_capacity_hours_per_month &&
    Number(formData.dev_capacity_hours_per_month) > 0;

  const handleBackdropClick = () => {
    if (!loading) onClose();
  };

  const selectedAccountMatchesActive = formData.jira_account_id && activeJiraAccount?.id === Number(formData.jira_account_id);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-project-title"
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-800 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <h3 id="create-project-title" className="text-xl font-semibold text-slate-900 dark:text-white mb-5">
            {isEditMode ? 'Edit Project' : 'Create New Project'}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Project name */}
            <div>
              <label htmlFor="project-name" className="block mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">
                Project name *
              </label>
              <input
                id="project-name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors.name ? 'border-red-500' : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white'
                  }`}
                placeholder="e.g. PM Tool"
              />
              {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
            </div>

            {/* Jira account */}
            <div>
              <label htmlFor="jira-account-select" className="block mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">
                Jira account *
              </label>
              {isEditMode ? (
                <div className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-sm">
                  {editProject.jira_account_name || accounts.find(a => String(a.id) === formData.jira_account_id)?.name || `Account ${editProject.jira_account_id}`}
                </div>
              ) : (
                <select
                  id="jira-account-select"
                  value={formData.jira_account_id}
                  onChange={(e) => {
                    setJiraProjectSearch('');
                    setFormData((p) => ({
                      ...p,
                      jira_account_id: e.target.value,
                      jira_project_key: '',
                      jira_project_name: '',
                      start_date: '',
                    }));
                    setJiraActualStartDate('');
                  }}
                  disabled={loadingAccounts}
                  className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors.jira_account_id ? 'border-red-500' : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white'
                    }`}
                >
                  <option value="" disabled>
                    {loadingAccounts ? 'Loading accounts...' : 'Select a Jira account'}
                  </option>
                  {accounts.map((acc) => (
                    <option key={acc.id} value={acc.id}>
                      {acc.name || `Account ${acc.id}`}
                    </option>
                  ))}
                </select>
              )}
              {!selectedAccountMatchesActive && formData.jira_account_id && (
                <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                  Activate this account in Settings to load its Jira projects.
                </p>
              )}
              {errors.jira_account_id && <p className="mt-1 text-xs text-red-500">{errors.jira_account_id}</p>}
            </div>

            {/* Jira project (searchable) */}
            <div className="relative" ref={jiraProjectRef}>
              <label htmlFor="jira-project-search" className="block mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">
                Jira project *
              </label>
              <div className="relative">
                {isEditMode ? (
                  <div className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-sm">
                    {editProject.jira_project_name || editProject.jira_project_key}
                  </div>
                ) : (
                  <>
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-slate-400" />
                    <input
                      id="jira-project-search"
                      type="text"
                      role="combobox"
                      aria-expanded={showJiraProjectDropdown}
                      aria-controls="jira-project-listbox"
                      aria-autocomplete="list"
                      value={jiraProjectSearch}
                      onChange={(e) => {
                        setJiraProjectSearch(e.target.value);
                        setShowJiraProjectDropdown(true);
                      }}
                      onFocus={() => setShowJiraProjectDropdown(true)}
                      disabled={!selectedAccountMatchesActive || loadingProjects}
                      placeholder={loadingProjects ? 'Loading projects...' : 'Search projects...'}
                      className={`w-full pl-9 pr-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors.jira_project_key ? 'border-red-500' : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white'
                        }`}
                    />
                    {showJiraProjectDropdown && filteredJiraProjects.length > 0 && (
                      <ul
                        id="jira-project-listbox"
                        role="listbox"
                        aria-label="Jira projects"
                        className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-lg py-1"
                      >
                        {filteredJiraProjects.map((p) => {
                          const isExisting = p.isExisting;
                          return (
                            <li
                              key={p.key || p.id}
                              role="option"
                              aria-selected={formData.jira_project_key === (p.key || p.id)}
                              aria-disabled={isExisting}
                            >
                              <button
                                type="button"
                                disabled={isExisting}
                                onClick={() => !isExisting && handleSelectJiraProject(p)}
                                className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between gap-2 ${isExisting
                                  ? 'cursor-not-allowed text-slate-400 dark:text-slate-500 bg-slate-50 dark:bg-slate-800/80'
                                  : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-900 dark:text-white'
                                  }`}
                              >
                                <span><span className="font-medium">{p.key || p.id}</span> – {p.name}</span>
                                {isExisting && (
                                  <span className="text-xs text-slate-400 dark:text-slate-500 shrink-0" title="This project is already added">
                                    Already added
                                  </span>
                                )}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </>
                )}
              </div>
              {errors.jira_project_key && <p className="mt-1 text-xs text-red-500">{errors.jira_project_key}</p>}
            </div>

            {/* Jira actual start date - read only */}
            <div>
              <label className="block mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">
                Project Start Date
              </label>
              <div className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-sm">
                {loadingStartDate ? (
                  <span className="text-slate-400">Fetching from Jira...</span>
                ) : (
                  jiraActualStartDate || '—'
                )}
              </div>
              <p className="mt-1 text-xs text-indigo-500 dark:text-indigo-400">
                Jira actual date
              </p>
            </div>

            {/* Plan View Start Date - editable */}
            <div>
              <label htmlFor="start-date" className="block mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">
                Plan View Start Date
              </label>
              <input
                id="start-date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData((p) => ({ ...p, start_date: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="mt-1 text-xs text-indigo-500 dark:text-indigo-400">
                Start date to calculate first week of capacity view and plan view
              </p>
            </div>

            {/* Project End Date */}
            <div>
              <label htmlFor="end-date" className="block mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">
                Project End Date <span className="text-slate-500">(optional)</span>
              </label>
              <input
                id="end-date"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData((p) => ({ ...p, end_date: e.target.value }))}
                min={formData.start_date}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="mt-1 text-xs text-indigo-500 dark:text-indigo-400">
                End date to calculate last day for capacity and plan view
              </p>
            </div>
            {/* Week start day */}
            <div>
              <label htmlFor="week-start-day" className="block mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">
                Week starts on
              </label>
              <select
                id="week-start-day"
                value={formData.week_start_day}
                onChange={(e) => setFormData((p) => ({ ...p, week_start_day: Number(e.target.value) }))}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value={0}>Sunday</option>
                <option value={1}>Monday</option>
                <option value={2}>Tuesday</option>
                <option value={3}>Wednesday</option>
                <option value={4}>Thursday</option>
                <option value={5}>Friday</option>
                <option value={6}>Saturday</option>
              </select>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                This determines how weeks are calculated in the capacity table.
              </p>
            </div>
            {/* Development capacity */}
            <div>
              <label htmlFor="dev-capacity-hours-per-month" className="block mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">
                Development capacity (hours per month) *
              </label>
              <input
                id="dev-capacity-hours-per-month"
                type="number"
                min={1}
                step={1}
                value={formData.dev_capacity_hours_per_month}
                onChange={(e) => setFormData((p) => ({ ...p, dev_capacity_hours_per_month: e.target.value }))}
                className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors.dev_capacity_hours_per_month ? 'border-red-500' : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white'
                  }`}
                placeholder="e.g. 160"
              />
              {errors.dev_capacity_hours_per_month && (
                <p className="mt-1 text-xs text-red-500">{errors.dev_capacity_hours_per_month}</p>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3 justify-end pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2.5 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!isFormValid || loading}
                className="px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <ArrowPathIcon className="size-4 animate-spin mr-2" />
                    {isEditMode ? 'Saving...' : 'Creating...'}
                  </>
                ) : (
                  isEditMode ? 'Save Changes' : 'Create Project'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
